<div class="h-full min-h-0 flex flex-row gap-4 w-full">
    <aside
        class="w-1/3 min-w-0 min-h-0 flex flex-col rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
        <div class="shrink-0 p-3 border-b border-zinc-200 dark:border-zinc-800">
            <label for="agency-insights-search" class="sr-only">Search agencies</label>
            <div class="grid grid-cols-1">
                <input id="agency-insights-search" type="search" autocomplete="off" placeholder="Search agencies…"
                    class="col-start-1 row-start-1 block w-full rounded-lg py-2 pr-3 pl-10 text-sm text-primary input-primary-icon"
                    [value]="searchQuery()" (input)="onSearchInput($any($event.target).value)" />
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-secondary sm:size-4"
                    aria-hidden="true">
                    <path d="m21 21-4.34-4.34" />
                    <circle cx="11" cy="11" r="8" />
                </svg>
            </div>
            <p class="mt-2 text-xs text-secondary m-0">
                {{ agenciesCount() }} agenc{{ agenciesCount() === 1 ? 'y' : 'ies' }} total
                @if (searchQuery().trim()) {
                    <span class="text-primary"> · {{ (agencies() ?? []).length }} shown</span>
                }
            </p>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto p-2 scrollbar-transparent" (scroll)="onListScroll($event)">
            @if (agencies() === undefined) {
            <p class="px-2 py-6 text-center text-sm text-secondary m-0">Loading agencies…</p>
            } @else if ((agencies() ?? []).length === 0 && !agenciesLoading()) {
            <p class="px-2 py-6 text-center text-sm text-secondary m-0">No agencies match your search.</p>
            } @else {
            @if (agenciesLoading()) {
            <p class="px-2 pb-2 text-center text-xs text-secondary m-0">Updating…</p>
            }
            <ul class="flex flex-col gap-1" role="listbox" aria-label="Agencies">
                @for (agency of agencies() ?? []; track agency.id) {
                <li role="option" [attr.aria-selected]="isSelectedAgency(agency)">
                    <button type="button"
                        class="group flex w-full flex-row items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors cursor-pointer"
                        [class]="
                  isSelectedAgency(agency)
                    ? 'border-primary/50 bg-primary/10 ring-1 ring-primary/35 dark:border-primary/45 dark:bg-primary/15 dark:ring-primary/25'
                    : 'border-transparent hover:border-zinc-200 hover:bg-zinc-50 dark:hover:border-zinc-700 dark:hover:bg-zinc-900/80'
                " (click)="onAgencySelect(agency)">
                        <div
                            class="shrink-0 size-10 overflow-hidden rounded-full bg-zinc-100 ring-1 ring-zinc-200/80 dark:bg-zinc-800 dark:ring-zinc-700 flex items-center justify-center">
                            <p class="m-0 text-sm font-semibold text-primary">{{ agencyInitial(agency) }}</p>
                        </div>
                        <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                            <p class="truncate text-sm font-medium text-primary leading-snug m-0">
                                {{ agency.name || 'Unnamed agency' }}
                            </p>
                            <p class="text-xs text-secondary m-0">{{ timeSinceCreated(agency.createdDate) }}</p>
                        </div>
                    </button>
                </li>
                }
            </ul>
            @if (agenciesLoadingMore()) {
            <p class="py-3 text-center text-xs text-secondary m-0">Loading more agencies…</p>
            }
            }
        </div>
    </aside>

    @if (selectedAgency(); as agency) {
    <section
        class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-zinc-200 shadow-sm dark:border-zinc-800">
        <div class="px-2 py-2 flex-0 flex flex-row items-center justify-between drawer-header-primary">
            <div class="flex items-center gap-x-4 min-w-0">
                <div
                    class="shrink-0 size-10 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                    <p class="m-0 text-sm font-semibold text-primary">{{ agencyInitial(agency) }}</p>
                </div>
                <div class="min-w-0">
                    <h3 class="text-base font-semibold text-gray-900 dark:text-white m-0 truncate">{{ agency.name }}
                    </h3>
                    <p class="text-sm text-gray-500 dark:text-gray-400 m-0 truncate">{{ agency.id }}</p>
                </div>
            </div>

            <nav aria-label="Agency insight sections" class="flex flex-row justify-center space-x-4 shrink-0">
                <button type="button" (click)="setSelectedTab('profile')"
                    [attr.aria-current]="selectedTab() === 'profile' ? 'page' : null"
                    class="group inline-flex items-center border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out overflow-hidden bg-transparent cursor-pointer"
                    [class]="getSelectedTabClass('profile')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4 shrink-0 transition-all duration-200 ease-in-out group-hover:mr-1.5"
                        [class]="getSelectedTabIconClass('profile') + (selectedTab() === 'profile' ? ' mr-1.5' : '')"
                        aria-hidden="true">
                        <path d="M10 12h4" />
                        <path d="M10 8h4" />
                        <path d="M14 21v-3a2 2 0 0 0-4 0v3" />
                        <path d="M6 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2" />
                        <path d="M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16" />
                    </svg>
                    <span class="transition-all duration-200 ease-in-out whitespace-nowrap"
                        [class]="selectedTab() === 'profile' ? 'opacity-100 max-w-xs' : 'opacity-0 max-w-0 group-hover:opacity-100 group-hover:max-w-xs'">
                        Profile
                    </span>
                </button>
                <button type="button" (click)="setSelectedTab('billing')"
                    [attr.aria-current]="selectedTab() === 'billing' ? 'page' : null"
                    class="group inline-flex items-center border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out overflow-hidden bg-transparent cursor-pointer"
                    [class]="getSelectedTabClass('billing')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4 shrink-0 transition-all duration-200 ease-in-out group-hover:mr-1.5"
                        [class]="getSelectedTabIconClass('billing') + (selectedTab() === 'billing' ? ' mr-1.5' : '')"
                        aria-hidden="true">
                        <path d="M12 18H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5" />
                        <path d="M18 12h.01" />
                        <path d="M19 22v-6" />
                        <path d="m22 19-3-3-3 3" />
                        <path d="M6 12h.01" />
                        <circle cx="12" cy="12" r="2" />
                    </svg>
                    <span class="transition-all duration-200 ease-in-out whitespace-nowrap"
                        [class]="selectedTab() === 'billing' ? 'opacity-100 max-w-xs' : 'opacity-0 max-w-0 group-hover:opacity-100 group-hover:max-w-xs'">
                        Billing
                    </span>
                </button>
                <button type="button" (click)="setSelectedTab('team')"
                    [attr.aria-current]="selectedTab() === 'team' ? 'page' : null"
                    class="group inline-flex items-center border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out overflow-hidden bg-transparent cursor-pointer"
                    [class]="getSelectedTabClass('team')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4 shrink-0 transition-all duration-200 ease-in-out group-hover:mr-1.5"
                        [class]="getSelectedTabIconClass('team') + (selectedTab() === 'team' ? ' mr-1.5' : '')"
                        aria-hidden="true">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                        <path d="M16 3.128a4 4 0 0 1 0 7.744" />
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                        <circle cx="9" cy="7" r="4" />
                    </svg>
                    <span class="transition-all duration-200 ease-in-out whitespace-nowrap"
                        [class]="selectedTab() === 'team' ? 'opacity-100 max-w-xs' : 'opacity-0 max-w-0 group-hover:opacity-100 group-hover:max-w-xs'">
                        Team
                    </span>
                </button>
                <button type="button" (click)="setSelectedTab('brands')"
                    [attr.aria-current]="selectedTab() === 'brands' ? 'page' : null"
                    class="group inline-flex items-center border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out overflow-hidden bg-transparent cursor-pointer"
                    [class]="getSelectedTabClass('brands')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4 shrink-0 transition-all duration-200 ease-in-out group-hover:mr-1.5"
                        [class]="getSelectedTabIconClass('brands') + (selectedTab() === 'brands' ? ' mr-1.5' : '')"
                        aria-hidden="true">
                        <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
                        <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9" />
                        <path d="M12 3v6" />
                    </svg>
                    <span class="transition-all duration-200 ease-in-out whitespace-nowrap"
                        [class]="selectedTab() === 'brands' ? 'opacity-100 max-w-xs' : 'opacity-0 max-w-0 group-hover:opacity-100 group-hover:max-w-xs'">
                        Brands
                    </span>
                </button>
            </nav>
        </div>

        <div class="flex min-h-0 flex-1 flex-col overflow-hidden p-2 scrollbar-transparent">
            @switch (selectedTab()) {
            @case ('profile') {
            <agency-ins-profile [agency]="agency" (agencyUpdated)="onAgencyProfileUpdated($event)"></agency-ins-profile>
            }
            @case ('billing') {
            <agency-ins-billing [agency]="agency"></agency-ins-billing>
            }
            @case ('team') {
            <agency-ins-team [agency]="agency"></agency-ins-team>
            }
            @case ('brands') {
            <agency-ins-brands [agency]="agency"></agency-ins-brands>
            }
            }
        </div>
    </section>
    } @else {
    <section
        class="flex-1 min-w-0 min-h-0 rounded-xl border border-dashed border-zinc-300 bg-zinc-50/50 dark:border-zinc-700 dark:bg-zinc-900/30 flex items-center justify-center p-6">
        <app-empty-state title="No agency selected"
            description="Select an agency from the list to view profile, billing, team and brands." />
    </section>
    }
</div>
